"use client";

import { CalendarDate } from "@internationalized/date";
import { format } from "date-fns";
import { enUS as enUSLocale } from "date-fns/locale/en-US";
import { nl as nlLocale } from "date-fns/locale/nl";

import * as React from "react";
import type { DateValue } from "react-aria-components";
import { cn } from "../lib/utils";
import { Calendar } from "./calendar";
import { Modal } from "./modal";

interface DatePickerModalProperties {
  isOpen: boolean;
  onClose: () => void;
  selectedDate?: Date;
  onSelect: (date: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  locale?: "nl" | "en";
  label?: string;
  className?: string;
}

// Helper to convert Date to CalendarDate
const dateToCalendarDate = (date: Date): CalendarDate => {
  return new CalendarDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
};

// Helper to convert CalendarDate to Date (using local timezone, not UTC)
const calendarDateToDate = (date: CalendarDate): Date => {
  // Create date at noon local time to avoid timezone issues when converting
  // This ensures the date stays the same regardless of timezone
  return new Date(date.year, date.month - 1, date.day, 12, 0, 0, 0);
};

export function DatePickerModal({
  isOpen,
  onClose,
  selectedDate,
  onSelect,
  minDate,
  maxDate,
  locale = "nl",
  label,
  className,
}: DatePickerModalProperties) {
  const [selected, setSelected] = React.useState<DateValue | null>(
    selectedDate ? dateToCalendarDate(selectedDate) : null,
  );

  React.useEffect(() => {
    if (selectedDate) {
      setSelected(dateToCalendarDate(selectedDate));
    }
  }, [selectedDate]);

  const handleSelect = (date: DateValue | null) => {
    setSelected(date);
    if (date) {
      const jsDate = calendarDateToDate(date as CalendarDate);
      onSelect(jsDate);
      // Close modal after selection on mobile
      if (window.innerWidth < 640) {
        setTimeout(() => {
          onClose();
        }, 200);
      }
    }
  };

  const dateLocale = locale === "nl" ? nlLocale : enUSLocale;

  const formattedDate = selected
    ? format(calendarDateToDate(selected as CalendarDate), "PPP", {
        locale: dateLocale,
      })
    : "";

  const calendarValue = selected;
  const minCalendarValue = minDate ? dateToCalendarDate(minDate) : undefined;
  const maxCalendarValue = maxDate ? dateToCalendarDate(maxDate) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={label}
      mobileOptimized={true}
      className={className}
    >
      <div className="space-y-6">
        {selected && (
          <div className="text-center pb-2 border-b border-gray-200">
            <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">
              {locale === "nl" ? "Geselecteerde datum" : "Selected Date"}
            </p>
            <p className="text-2xl font-extrabold text-boldness">
              {formattedDate}
            </p>
          </div>
        )}
        <div className="w-full">
          <Calendar
            value={calendarValue}
            onChange={handleSelect}
            minValue={minCalendarValue}
            maxValue={maxCalendarValue}
            className={cn("w-full", className)}
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {locale === "nl" ? "Sluiten" : "Close"}
          </button>
          {selected && (
            <button
              onClick={() => {
                if (calendarValue) {
                  const jsDate = calendarDateToDate(
                    calendarValue as CalendarDate,
                  );
                  onSelect(jsDate);
                }
                onClose();
              }}
              className="flex-1 rounded-xl bg-strategy-gold px-4 py-3 text-sm font-semibold text-boldness transition-colors hover:bg-strategy-gold/90"
            >
              {locale === "nl" ? "Bevestigen" : "Confirm"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
